import React, { useEffect, useRef, useState } from "react";
import styles from './Evaluation.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../../contexts/ToastContext";
import axiosClient from "../../../api/axiosClient";
import Tag from "../../../components/tag/Tag";
import EvaluationResponse from "../../../components/evaluationResponse/EvaluationResponse";
import Icon from "../../../components/icon/Icon";
import Button from "../../../components/button/Button";
import EvaluationPagination from "../../../components/evaluationPagination/EvaluationPagination";
import EvaluationNotes from "../../../components/evaluationNotes/EvaluationNotes";
import Tooltip from "../../../components/tooltip/Tooltip";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import useDeviceType from "../../../hooks/useDeviceType";
import Modal from "../../../components/modal/Modal";
import BtTable from "../../../components/btTable/BtTable";
import { useAuth } from "../../../contexts/AuthContext";

const Evaluation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [isCommentActive, setIsCommentActive] = useState(false);
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [isCommentLoading,  setIsCommentLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [isNoteLoading,  setIsNoteLoading] = useState(false);
  const [isCompleted,  setIsCompleted] = useState(false);
  const [isBenchmarksOpened, setIsBenchmarksOpened] = useState(false);
  const [benchmarksData, setBenchmarksData] = useState(null);
  const [note, setNote] = useState('');
  const { evaluationId } = useParams();
  const { showToast } = useToasts();
  const actionsRef = useRef(null);
  const questionRef = useRef(null);
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const { role } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`evaluations/${evaluationId}`)
      .then((response) => {
        if(response.data) {
          setStudent({...response.data?.student});
          setQuestions(response.data?.questions);
          setNote(response.data?.note);
          setIsCompleted(response.data?.status === 'Completed');
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, [evaluationId, showToast]);

  useEffect(() => {
    if(questions[currentQuestion-1]) {
      const selectedQuestion = questions[currentQuestion - 1];
      setActiveQuestion(selectedQuestion);
      setComment(selectedQuestion?.comment);
      setIsCommentActive(false);
    }
  }, [questions, currentQuestion]);

  const handleSaveAnswer = async (newAnswer) => {
    if(isCompleted) {
      return false;
    }
    const oldAnswer = activeQuestion.answer
    setQuestions(prevQuestions => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[currentQuestion - 1].answer = {
        id: newAnswer
      };
      return updatedQuestions;
    });
    await axiosClient.patch(`evaluations/${evaluationId}/answer`, {
      questionId: activeQuestion?.id,
      answerId: newAnswer
    })
    .catch((error) => {
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[currentQuestion - 1].answer = oldAnswer;
        return updatedQuestions;
      });
      showToast(error.message, 'error');
    });
  }

  const handleSaveComment = async () => {
    if(isCompleted) {
      return false;
    }
    setIsCommentLoading(true);
    await axiosClient.patch(`evaluations/${evaluationId}/comment`, {
      questionId: activeQuestion?.id,
      comment: comment
    })
    .then((data) => {
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[currentQuestion - 1].comment = comment;
        return updatedQuestions;
      });
      showToast(data.message);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsCommentLoading(false);
    });
  };

  const handleSaveNote = async (newNote) => {
    if(isCompleted) {
      return false;
    }
    setIsNoteLoading(true);
    await axiosClient.patch(`evaluations/${evaluationId}/note`, {
      note: newNote
    })
    .then((data) => {
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[currentQuestion - 1].comment = comment;
        return updatedQuestions;
      });
      setNote(newNote);
      showToast(data.message);
      setIsNoteVisible(false);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsNoteLoading(false);
    });
  };

  const handleClickBenchmark = async () => {
    setIsBenchmarksOpened(true);
    if(benchmarksData === null && student) {
      await axiosClient.get(`benchmarks`, {
        params: {
          studentId: student.id,
        }
      })
      .then((response) => {
        setBenchmarksData(response.data);
      })
      .catch((error) => {
        showToast(error.message, 'error');
      });
    }
  }

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs 
          breadcrumbs={[
            { label: 'Home', path: '/' },
            ...(student && role === 'Instructor' ? [
              { 
                label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, 
                path: `/student-overview/${student.id ?? ''}` 
              }
            ] : []),
            ...(role === 'Student' ? [
              {
                label: `Evaluations`,
                path: `/evaluations`
              }
            ] : []),
            { label: `Evaluation ID - ${evaluationId}` }
          ]}
        />
        <div className={styles.cta}>
          <button onClick={handleClickBenchmark}><Icon name="award" />Benchmarks</button>
          <button onClick={() => setIsNoteVisible(!isNoteVisible)}><Icon name="clipboard" />Confidential Notes</button>
          {isNoteVisible && <EvaluationNotes
            note={note}
            onSave={handleSaveNote}
            onClose={() => setIsNoteVisible(false)}
            isLoading={isNoteLoading}
            disabled={isCompleted}
          />}
        </div>
      </div>
      {activeQuestion && <>
        <div className={styles.scrollContainer}>
          <div className={styles.section} ref={questionRef}>
            <div className={styles.number}>{currentQuestion}.</div>
            <div className={styles.content}>
              <div className={styles.question}>
                <h2>{activeQuestion?.question?.type}</h2>
                <p>{activeQuestion?.question?.question}</p>
                {activeQuestion?.question?.tags && (
                  <div className={styles.competency}>
                    <span className={styles.help}>This question evaluates these competencies</span>
                    <div className={styles.tags}>
                      {activeQuestion.question.tags.map((tag, index) => (
                        <Tag
                          key={tag}
                          text={tag}
                          variant={index + 1}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className={styles.response}>
                  <EvaluationResponse
                    options={activeQuestion?.options || []}
                    selected={activeQuestion?.answer?.id || null}
                    onChange={handleSaveAnswer}
                  />
                </div>
                <div className={styles.comment}>
                  <label>
                    Comments
                    <Tooltip
                      content="These comments will be viewable by the student. Please consider providing additional feedback that pertains to the competencies for this question."
                      position={isMobile ? 'bottom' : 'right'}
                    />
                  </label>
                  <textarea
                    placeholder="Add Comment"
                    className={isCommentActive ? styles.active : ''}
                    value={comment || ''}
                    onClick={() => {
                      setIsCommentActive(true);
                        setTimeout(() => {
                        if (actionsRef.current) {
                          actionsRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        }
                      }, 100);
                    }}
                    onChange={({ target: { value } })=> {
                      setComment(value);
                    }}
                    disabled={isCompleted}
                  />
                  {isCommentActive && <div className={styles.actions} ref={actionsRef}>
                  {isCommentLoading ? 
                    <LoadingSpinner size={20} /> : <>
                    <Button 
                      label="Cancel"
                      size="small"
                      variant="secondary"
                      onClick={() => {
                        setIsCommentActive(false);
                        setComment(activeQuestion?.comment)
                      }}
                    />
                    <Button 
                      label="Save"
                      size="small"
                      onClick={handleSaveComment}
                      disabled={!comment}
                    />
                    </>}
                  </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pagination}>
          <EvaluationPagination
            currentQuestion={currentQuestion}
            totalQuestions={questions.length}
            attendedQuestion={questions.map((item, index) => (item.answer !== null ? index + 1 : -1))
                .filter(index => index !== -1)}
            onChange={(questionNumber) => {
              if (questionRef.current) {
                questionRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }
              setCurrentQuestion(questionNumber)
            }}
          />
          {!isCompleted && <Button
            label="Continue"
            disabled={questions.filter(item => item.answer === null).length > 0}
            onClick={() => {
              if(role === 'Instructor') {
                navigate(`/student-overview/evaluation/${evaluationId}/summary`)
              } else {
                navigate(`/evaluations/${evaluationId}/summary`)
              }
            }}
          />}
        </div>
      </>}
      {isBenchmarksOpened && <Modal>
        <BtTable
          data={benchmarksData}
          onCancel={() => setIsBenchmarksOpened(false)}
          isAcknowledged={true}
        />
      </Modal>}
    </div>
  );
};

export default Evaluation;